import { ApiReqBaseUrl, resolveApiUrl } from "../../../assets/GlobalVariables";
import { ORGANIZED_TOUR_QUOTE_PDF_POST_PATH } from "../constants/apiEndpoints";
import { notifyIfNativeFetchResponse } from "../../../services/sessionInvalidation";

function parseFilenameFromContentDisposition(cd) {
  if (!cd || typeof cd !== "string") return null;
  const star = /filename\*=UTF-8''([^;\n]+)/i.exec(cd);
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
    } catch {
      return star[1].trim().replace(/^"|"$/g, "") || null;
    }
  }
  const plain = /filename="([^"]+)"/i.exec(cd);
  if (plain && plain[1]) {
    return plain[1].trim();
  }
  return null;
}

/**
 * הורדת PDF הצעה/אישור להזמנת LDS מטיול מאורגן — fetch כ-blob עם בדיקת application/pdf.
 *
 * @param {{ ldsOrderId: string, totalPriceIls?: number|null }} opts
 */
export async function downloadOrganizedTourLdsOfferPdf({ ldsOrderId, totalPriceIls = null }) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log("[ORG_PDF_RENDER_START]", { branch: "lds_order_download" });
  }
  const id = String(ldsOrderId || "").trim();
  if (!id) {
    // eslint-disable-next-line no-console
    console.log("[ORG_TOUR_PDF_ERROR]", { reason: "missing_lds_order_id" });
    if (import.meta.env.DEV) console.log("[ORG_PDF_RENDER_FAILED]", { reason: "missing_lds_order_id" });
    throw new Error("חסר מזהה הזמנה — יש לשמור הצעה לפני הורדת PDF");
  }

  // eslint-disable-next-line no-console
  console.log("[ORG_TOUR_PDF_FROM_EXISTING_ORDER]", { ldsOrderId: id });
  // eslint-disable-next-line no-console
  console.log("[ORG_TOUR_PDF_NO_ORDER_CREATE]", { branch: "existing_lds_order_get_pdf" });

  const qp =
    Number.isFinite(Number(totalPriceIls)) && Number(totalPriceIls) > 0
      ? `?totalPrice=${encodeURIComponent(String(totalPriceIls))}`
      : "";
  const url = `${ApiReqBaseUrl}/lds_orders/${encodeURIComponent(id)}/download-pdf${qp}`;

  // eslint-disable-next-line no-console
  console.log("[ORG_TOUR_PDF_CLICK]", { ldsOrderId: id });
  // eslint-disable-next-line no-console
  console.log("[ORG_TOUR_PDF_ENDPOINT]", {
    path: `/lds_orders/${id}/download-pdf`,
    hasTotalPrice: Number.isFinite(Number(totalPriceIls)) && Number(totalPriceIls) > 0,
  });

  let res;
  try {
    res = await fetch(url, { method: "GET", credentials: "include" });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log("[ORG_TOUR_PDF_ERROR]", {
      phase: "network",
      message: err?.message || String(err),
    });
    throw new Error(err?.message || "שגיאת רשת בהורדת PDF");
  }

  void notifyIfNativeFetchResponse(res, null);

  const contentType = res.headers.get("content-type") || "";
  // eslint-disable-next-line no-console
  console.log("[ORG_TOUR_PDF_RESPONSE]", {
    status: res.status,
    contentType,
    ok: res.ok,
  });

  let buf;
  try {
    buf = await res.arrayBuffer();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log("[ORG_TOUR_PDF_ERROR]", { phase: "read_body", message: err?.message });
    throw new Error("לא ניתן לקרוא את תגובת השרת");
  }

  const size = buf.byteLength;
  // eslint-disable-next-line no-console
  console.log("[ORG_TOUR_PDF_BLOB_READY]", { size, contentType });

  const jsonErrFromText = (text) => {
    const t = String(text || "").trim();
    if (!t) return null;
    try {
      const j = JSON.parse(t);
      return j?.error || j?.message || null;
    } catch {
      return null;
    }
  };

  if (!res.ok) {
    const text = new TextDecoder().decode(buf);
    const parsed = jsonErrFromText(text);
    const msg = parsed || `שגיאת שרת (${res.status})`;
    // eslint-disable-next-line no-console
    console.log("[ORG_TOUR_PDF_ERROR]", { status: res.status, message: msg });
    throw new Error(msg);
  }

  const ctLower = contentType.toLowerCase();
  const u8 = new Uint8Array(buf.slice(0, 4));
  const magicPdf = u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46;
  const looksPdf = ctLower.includes("application/pdf") || magicPdf;

  if (!looksPdf || size < 64) {
    const text = new TextDecoder().decode(buf);
    // eslint-disable-next-line no-console
    console.log("[ORG_TOUR_PDF_INVALID_RESPONSE]", {
      contentType,
      size,
      preview: text.slice(0, 280),
    });
    const fromJson = jsonErrFromText(text);
    throw new Error(
      fromJson ||
        (text.trim().toLowerCase().startsWith("<!doctype") || text.trim().startsWith("<html")
          ? "התקבלה תצוגת HTML במקום PDF (ייתכן שחסר wkhtmltopdf בשרת). אנא צרו קשר עם התמיכה."
          : "השרת החזיר קובץ שאינו PDF תקין.")
    );
  }

  const blob = new Blob([buf], { type: "application/pdf" });
  const cd = res.headers.get("content-disposition") || "";
  let filename =
    parseFilenameFromContentDisposition(cd) ||
    `organized-tour-offer.pdf`;

  // eslint-disable-next-line no-console
  console.log("[ORG_TOUR_PDF_DOWNLOAD_START]", { filename, size });

  const objUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log("[ORG_PDF_RENDER_SUCCESS]", { branch: "lds_order_download", filename, size });
    }
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

/**
 * PDF מהצגת פרופיל בלי שורת lds_orders — POST snapshot בלבד.
 *
 * @param {Record<string, unknown>} body — כולל sessionID + snapshot (buildOrganizedTourQuoteSnapshotPayload.payload)
 */
export async function downloadOrganizedTourQuoteSnapshotPdf(body) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log("[ORG_PDF_RENDER_START]", { branch: "quote_snapshot_post" });
  }
  if (!body || typeof body !== "object") {
    if (import.meta.env.DEV) console.log("[ORG_PDF_RENDER_FAILED]", { reason: "missing_body" });
    throw new Error("חסר מידע להפקת PDF");
  }
  const url = resolveApiUrl(ORGANIZED_TOUR_QUOTE_PDF_POST_PATH);
  // eslint-disable-next-line no-console
  console.log("[ORG_TOUR_PDF_FROM_DEAL_SNAPSHOT]", { path: ORGANIZED_TOUR_QUOTE_PDF_POST_PATH });
  // eslint-disable-next-line no-console
  console.log("[ORG_TOUR_PDF_NO_ORDER_CREATE]", { branch: "quote_pdf_post" });

  let res;
  try {
    res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Accept: "application/pdf, application/json",
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new Error(err?.message || "שגיאת רשת בהורדת PDF");
  }

  void notifyIfNativeFetchResponse(res, null);

  const buf = await res.arrayBuffer();
  const contentType = res.headers.get("content-type") || "";

  const jsonErrFromText = (text) => {
    const t = String(text || "").trim();
    if (!t) return null;
    try {
      const j = JSON.parse(t);
      return j?.message || j?.error || null;
    } catch {
      return null;
    }
  };

  if (!res.ok) {
    const text = new TextDecoder().decode(buf);
    const msg = jsonErrFromText(text) || `שגיאת שרת (${res.status})`;
    throw new Error(msg);
  }

  const ctLower = contentType.toLowerCase();
  const u8 = new Uint8Array(buf.slice(0, 4));
  const magicPdf = u8[0] === 0x25 && u8[1] === 0x50 && u8[2] === 0x44 && u8[3] === 0x46;
  const looksPdf = ctLower.includes("application/pdf") || magicPdf;
  const size = buf.byteLength;

  if (!looksPdf || size < 64) {
    const text = new TextDecoder().decode(buf);
    const fromJson = jsonErrFromText(text);
    throw new Error(
      fromJson ||
        (text.trim().toLowerCase().startsWith("<!doctype") || text.trim().startsWith("<html")
          ? "התקבלה תצוגת HTML במקום PDF (ייתכן שחסר wkhtmltopdf בשרת)."
          : "השרת החזיר קובץ שאינו PDF תקין.")
    );
  }

  const blob = new Blob([buf], { type: "application/pdf" });
  const cd = res.headers.get("content-disposition") || "";
  let filename = parseFilenameFromContentDisposition(cd) || "organized-tour-quote.pdf";
  const objUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log("[ORG_PDF_RENDER_SUCCESS]", { branch: "quote_snapshot_post", filename, size: buf.byteLength });
    }
  } finally {
    URL.revokeObjectURL(objUrl);
  }
}

/**
 * @param {{ ldsOrderId?: string|null, totalPriceIls?: number|null, quotePdfPostBody?: Record<string, unknown>|null }} opts
 */
export async function downloadOrganizedTourProfileOfferPdf({
  ldsOrderId = null,
  totalPriceIls = null,
  quotePdfPostBody = null,
}) {
  const id = ldsOrderId != null ? String(ldsOrderId).trim() : "";
  if (id) {
    return downloadOrganizedTourLdsOfferPdf({ ldsOrderId: id, totalPriceIls });
  }
  if (!quotePdfPostBody || typeof quotePdfPostBody !== "object") {
    throw new Error("חסרים פרטי הצעה להפקת PDF — יש לבחור טיול ולוודא מחיר");
  }
  return downloadOrganizedTourQuoteSnapshotPdf(quotePdfPostBody);
}
